import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ContactValidator} from '../../helpers/contact-validator';
import {CustomValidators} from '@narik/custom-validators';
import {ConfirmService, HttpLoaderService, SnackbarService, T4Validators} from '@tymes4-shared';
import {KeyValuePair} from '../../models/key-value-pair';
import {EditContactFormComponent} from '../edit-contact-form/edit-contact-form.component';
import {AuthService} from '../../services/auth.service';
import {
  AccountManagerModel,
  AccountManagerService,
  AddressModel,
  ConfigService,
  ContactService,
  CountryService,
  CustomerModel,
  CustomerParentRelationshipView,
  CustomerService,
  LanguageService,
  PostalCodeService,
  SystemConfigurationSettingsService
} from '../../api';
import { snackbarGoToNewCustomer } from '../../components/snackbar-go-to-new-customer/snackbar-go-to-new-customer.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';

@Component({
    selector: 'app-edit-customer-form',
    templateUrl: './edit-customer-form.component.html',
    styleUrls: ['./edit-customer-form.component.scss'],
    standalone: false
})

export class EditCustomerFormComponent implements OnInit {
  @ViewChild('pagerComponent') pagerComponent;
  @ViewChild('accountsPagerComponent') accountsPagerComponent;
  @ViewChild('accountsSearchComponent') accountsSearchComponent;

  customerId: number = 0;
  mainForm: FormGroup;
  PrimaryContact: FormGroup;
  Address: FormGroup;
  public customer: any = {};
  countries: any = null;
  accountManagers: Array<AccountManagerModel> = null;
  public selectedObjects: any = [];

  private businessTypeId = 1;

  isNewCustomer: boolean = false;
  isUsingSASSO: boolean = false;

  public saving = false;
  public isDirty = false;

  private requiredFields: KeyValuePair<boolean> = null;

  public customerTypes: Array<any> = [
    {viewValue: this.translate.instant('FANS.EDIT-CUSTOMER-FORM.COMPONENT.CUSTOMER-DETAILS.CUSTOMER-TYPE.FORM.DROPDOWN.BUSINESS-CUSTOMER'), value: 1},
    {viewValue: this.translate.instant('FANS.EDIT-CUSTOMER-FORM.COMPONENT.CUSTOMER-DETAILS.CUSTOMER-TYPE.FORM.DROPDOWN.PRIVATE-CUSTOMER'), value: 2},
  ];

  public selectableGenders: Array<any> = [
    {viewValue: this.translate.instant('FANS.EDIT-CUSTOMER-FORM.COMPONENT.CUSTOMER-DETAILS.PRIMARY-ACCOUNT.FORM.DROPDOWN.MALE-SALUTATION'), value: 'M'},
    {viewValue: this.translate.instant('FANS.EDIT-CUSTOMER-FORM.COMPONENT.CUSTOMER-DETAILS.PRIMARY-ACCOUNT.FORM.DROPDOWN.FEMALE-SALUTATION'), value: 'F'},
  ];

  public SelectableLanguages: Array<any> = [];

  public canDegradeAccountToUnderlying: boolean = false;

  validationMessages = {
    'DateOfBirth': [
      {type: 'maxDate', message: 'FORM.VALIDATION.INVALIDDOB'},
      {type: 'minDate', message: 'FORM.VALIDATION.INVALIDDOB'},

    ]
  };

  public allowProfileEdit: boolean = true;
  private allowSSOWriteBack: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public passedData: any,
              public dialogRef: MatDialogRef<EditCustomerFormComponent>,
              private countryService: CountryService,
              private customerService: CustomerService,
              private contactValidator: ContactValidator,
              private loader: HttpLoaderService,
              private snackBarGoToCustomer: MatSnackBar,
              private accountManagerService: AccountManagerService,
              private confirmService: ConfirmService,
              private snackBar: SnackbarService,
              private languageService: LanguageService,
              private translate: TranslateService,
              private postalcodeService: PostalCodeService,
              private auth: AuthService,
              private config: SystemConfigurationSettingsService,
              private contactService: ContactService,
              private configService: ConfigService) {
  }

  isRequiredField(field: string, targetForm: FormGroup = null): boolean {
    if (this.requiredFields === null)
      this.requiredFields = {};

    const form = targetForm ? targetForm : this.mainForm;
    const formField = form.get(field);
    if (!formField.validator) {
      return false;
    }

    const validator = formField.validator({} as AbstractControl);
    this.requiredFields[field] = validator && validator.required;

    formField.validator(formField);
    return this.requiredFields[field];
  }


  ngOnInit() {

    this.customerId = this.passedData.payload;
    this.isNewCustomer = this.passedData.isNew;

    // NOTE: when the customer type is changed, it is overwitten which controls are required and which are not.
    // For fans, the list of required fields is in a configuration setting.
    // For companies, the list of required fields is in the method subscribeToCustomerTypeChanges.

    this.mainForm = new FormGroup({
      Id: new FormControl(null),
      PaysByInvoice: new FormControl(), //here to preserve value
      Name: new FormControl('', [Validators.maxLength(512)]),
      COC: new FormControl(''),
      CustomerType: new FormControl('', [Validators.required]),
      AccountManagerId: new FormControl(null),
      TelephoneNr: new FormControl('', [T4Validators.validatePhonenumber]),
      ExternalCode: new FormControl(''),
      VATNr: new FormControl(''),
      ExternalId: new FormControl(''),
      InvoiceAddressId: new FormControl(null)
    });

    this.PrimaryContact = new FormGroup({
      Id: new FormControl(),
      CustomerId: new FormControl(),
      Initials: new FormControl('', [Validators.maxLength(20)]),
      FirstName: new FormControl('', [Validators.maxLength(50), Validators.required]),
      MiddleName: new FormControl('', [Validators.maxLength(50)]),
      LastName: new FormControl('', [Validators.maxLength(50), Validators.required]),
      Email: new FormControl(null, [CustomValidators.email], [this.contactValidator.emailTaken.bind(this)]),
      MobileNr: new FormControl('', [T4Validators.validatePhonenumber]),
      Gender: new FormControl(''),
      DateOfBirth: new FormControl(null, {
        updateOn: 'blur',
        validators: [CustomValidators.minDate(new Date(1900, 0, 1)), CustomValidators.maxDate(new Date())]
      }),
      Primary: new FormControl(true),
      Active: new FormControl(true),
      LanguageId: new FormControl(null),
      SSOId: new FormControl(),
    });

    this.Address = new FormGroup({
      Street: new FormControl('', [Validators.required]),
      StreetNr: new FormControl('', [CustomValidators.number, Validators.required]),
      StreetNrAddition: new FormControl(''),
      PostalCode: new FormControl('', [Validators.required]),
      CountryId: new FormControl('', [Validators.required]),
      City: new FormControl('', [Validators.required])
    });

    this.countryService.listAllCountries().subscribe((countries) => {
      // Get translated strings
      countries.map(c => c.Name = this.translate.instant(`COUNTRY.${c.Iso2}`));

      // Sort by prio and alphabetically
      this.countries = countries.sort((c1, c2) => {
        const prio1 = c1.Priority || 0;
        const prio2 = c2.Priority || 0;
        if (prio2 - prio1 !== 0)
          return (prio2 - prio1);

        // Remove non-letters from the names
        const name1 = c1.Name.replace(/[^a-z]/gi, '');
        const name2 = c2.Name.replace(/[^a-z]/gi, '');
        return (name1 < name2) ? -1 : (name1 > name2) ? 1 : 0;
      });
    });

    this.accountManagerService.listActiveAccountManagers().subscribe((data: Array<AccountManagerModel>) => {
      this.accountManagers = data;
    });

    this.languageService.getLanguages().subscribe((languages) => {
      this.SelectableLanguages = languages;
    });

    if (!this.isNewCustomer) {
      this.loadCustomerForEdit();
    } else {
      this.customer = {PrimaryContact: {}, Address: {}};
      this.mainForm.patchValue(this.customer);
      this.PrimaryContact.patchValue(this.customer.PrimaryContact);
      this.Address.patchValue(this.customer.Address);
      this.getSSOInfo();
    }

    this.subscribeToCustomerTypeChanges();
  }

  private updateRequiredFieldsFromConfig(configurationKey: string) {

    this.config.getCustomerFormRequiredFields().subscribe(fields => {
      const requiredFields = fields.split(';');
      this.setRequiredFields(this.mainForm, requiredFields);
      this.setRequiredFields(this.PrimaryContact, requiredFields);
      this.setRequiredFields(this.Address, requiredFields);
    });
  }

  private setRequiredFields(form: FormGroup, requiredFields: string[]) {
    for (let controlName in form.controls) {

      if (controlName === "CustomerType") {
        continue; // skip because updating this control would cause an infinite loop
      }

      let control = form.controls[controlName];

      if (requiredFields.includes(controlName)) {
        if (!control.hasValidator(Validators.required)) {
          control.addValidators([Validators.required]);
        }
      }
      else {
        control.removeValidators([Validators.required]);
      }

      control.updateValueAndValidity();
    }
  }

  private subscribeToCustomerTypeChanges() {
    this.mainForm.get('CustomerType').valueChanges.subscribe((type: number) => {
      if (type === this.businessTypeId) {
        this.setRequiredFields(this.mainForm, ["Name"]);
        this.setRequiredFields(this.PrimaryContact, ["FirstName", "LastName"]);
        this.setRequiredFields(this.Address, ["Street", "StreetNr", "PostalCode", "CountryId", "City"]);
      } else {
        this.updateRequiredFieldsFromConfig('Customerform.RequiredFields');
      }
    });
  }

  loadCustomerForEdit() {
    this.customer = {};
    this.customerService.getCustomerWithContacts(this.customerId).subscribe((data: CustomerModel) => {
      //set the customer on screen and load the address
      this.customer = data;

      this.contactValidator.currentEmail = this.customer.Email;
      this.mainForm.patchValue(this.customer);
      if (this.customer.PrimaryContact === null || this.customer.PrimaryContact === void 0) {
        this.customer.PrimaryContact = {};
      }
      this.PrimaryContact.patchValue(this.customer.PrimaryContact);
      this.Address.patchValue(this.customer.Address);

      // disable all fields when we use SSO
      this.determineAllowProfileEditing(data).subscribe(result => {
        this.allowProfileEdit = result;
        if (!this.allowProfileEdit) {
          this.disableMainformFields();
          this.PrimaryContact.disable();
          this.Address.disable();
        } 
        else {
          this.mainForm.enable();
          this.PrimaryContact.enable();
          this.Address.enable();
          if (data.PrimaryContact.SSOId){
            this.PrimaryContact.controls.Email.disable();
            this.PrimaryContact.controls.SSOId.disable();
            this.mainForm.controls.CustomerType.disable();
          }
        }
      })
      
      
    

      this.getSSOInfo();
    });

    this.customerService.getParent(this.customerId, false).subscribe((data: CustomerParentRelationshipView) => {
      if (data && !data["IsManagedByParent"]) {
          this.canDegradeAccountToUnderlying = true
      }
    });
  }

  disableMainformFields() {
    this.mainForm.controls.CustomerType.disable();
    this.mainForm.controls.TelephoneNr.disable();
    this.mainForm.controls.Name.disable();
  }

  checkPostalCode(forceCheck: boolean) {
    const postalcode = this.Address['controls'].PostalCode;
    const streetNr = this.Address['controls'].StreetNr;
    const addition = this.Address['controls'].StreetNrAddition;
    const doCall = ((postalcode.value && streetNr.value) && (postalcode.valid && streetNr.valid) && (postalcode.dirty || streetNr.dirty));

    if (doCall || forceCheck) {
      this.postalcodeService.checkPostalCode(postalcode.value, streetNr.value.toString(), addition?.value?.toString() ?? "").subscribe((address: AddressModel) => {
        if (address != null) {
          this.Address.patchValue(address);
        }
      });
    }
  }

  customerIsValidAndDirty() {
    const anyFieldDirty = this.mainForm.dirty || this.PrimaryContact.dirty || this.Address.dirty;
    if (!anyFieldDirty) {
      return false;
    }

    let isValid: boolean =
      (this.PrimaryContact.valid || this.PrimaryContact.disabled) &&
      (this.Address.valid || this.Address.disabled);

    const activeType = this.mainForm.get('CustomerType').value;
    if (activeType == this.businessTypeId) {
      isValid &&= this.mainForm.valid;
    }

    return isValid;
  }

  submit() {
    // mainForm may contain disabled controls, in which case mainForm.value does not include those properties.
    // However, the backend currently lacks validation and interprets missing properties as them being set to null, but we just want to keep the values unchanged for disabled controls.
    // Therefore, mainForm.getRawValue() is used here so that also the unchanged disabled fields are sent to the backend.
    const customer = this.mainForm.getRawValue();

    customer.PrimaryContact = this.PrimaryContact.getRawValue();
    customer.Address = this.Address.getRawValue();

    // Prevents email validation
    if (customer.PrimaryContact.Email == '') {
      customer.PrimaryContact.Email = null;
    }

    if (!this.isNewCustomer) {
      this.loader.open();
      this.saving = true;
      this.customerService.updateCustomer(customer).subscribe((data: boolean) => {
        this.loader.close();
        this.dialogRef.close(true);
        this.saving = false;
        this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.CUSTOMER-SAVED'), 'GENERIC.SNACKBAR.CLOSE');
      });
    } else {
      this.customerService.createCustomer(customer).subscribe((data: number) => {
        this.loader.close();
        this.dialogRef.close(data);
        this.saving = false;
                this.snackBarGoToCustomer.openFromComponent(snackbarGoToNewCustomer, {
                duration: 12500,
                data: data
              })
      });
    }
  }

  deleteSSOId() {
    const title = this.translate.instant('DIALOG.SSO_REMOVE.CONFIRM.TITLE');
    const message = this.translate.instant('DIALOG.SSO_REMOVE.CONFIRM.MSG');
    this.confirmService.confirm({ title: title, message: message, okonly: false}).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.customer.PrimaryContact.SSOId = null;
      this.PrimaryContact.patchValue(this.customer.PrimaryContact);
      this.PrimaryContact.markAsDirty();
      this.mainForm.enable();
      this.PrimaryContact.enable();
      this.Address.enable();
    });
  }

  getSSOInfo() {
    const user = this.auth.getLoggedInUser();
    if (user != null) {
      this.isUsingSASSO = user.SystemSettings['SSOEnabled'].toLowerCase() == 'true';
    }
  }

  degradeAccountToUnderlying() {
    const title = this.translate.instant('DIALOG.DEGRADE_ACCOUNT.CONFIRM.TITLE');
    const message = this.translate.instant('DIALOG.DEGRADE_ACCOUNT.CONFIRM.MSG');
    this.confirmService.confirm({ title: title, message: message, okonly: false}).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.loader.open();
      this.saving = true;
      this.contactService.degradeAccountToUnderlying(this.customerId).subscribe(contact => {
        this.loader.close();
        this.dialogRef.close(true);
        this.saving = false;
        this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.CUSTOMER-SAVED'), 'GENERIC.SNACKBAR.CLOSE');
        });
      });
  }
  determineAllowProfileEditing(customer: CustomerModel): Observable<boolean>{
    if (customer.PrimaryContact && customer.PrimaryContact.SSOId){
      return this.configService.allowSSOWriteBack();
    }
    return of(true);
  }
}
